import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import './ManualSale.css';
import { useRevit } from '../../contexts/RevitContext';
import { fetchOffers } from '../../utils/apiCalls';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'revenue-node-server-tuomaskivioja-tuomaskiviojas-projects.vercel.app';

const ManualSale = () => {
    const [email, setEmail] = useState('');
    const [selectedOffer, setSelectedOffer] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [offers, setOffers] = useState([]);
    const [matches, setMatches] = useState(null);
    const [knowResource, setKnowResource] = useState(false);
    const [resourceType, setResourceType] = useState('video');
    const [resourceInput, setResourceInput] = useState('');
    const [customConversionValue, setCustomConversionValue] = useState('');
    const [selectedStep, setSelectedStep] = useState('');
    const { getToken, userId } = useAuth();
    const { username } = useRevit();

    // Reuse same YouTube ID extraction logic as in Links.js
    const extractVideoId = (url) => {
        // Handle YouTube live links (format: youtube.com/live/VIDEO_ID)
        const liveRegex = /(?:https?:\/\/)?(?:www\.)?youtube\.com\/live\/([^&\n?#]+)/;
        const liveMatch = url.match(liveRegex);
        if (liveMatch) {
            return liveMatch[1];
        }
        // Handle standard YouTube links
        const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|.+\?v=)?|youtu\.be\/)([^&\n?#]+)/;
        const match = url.match(regex);
        return match ? match[1] : null;
    };

    // Fetch offers when component mounts
    React.useEffect(() => {
        const loadOffers = async () => {
            try {
                const token = await getToken();
                const result = await fetchOffers(username, token);
                setOffers(result.offers);
            } catch (error) {
                console.error('Error fetching offers:', error);
            }
        };

        if (username) {
            loadOffers();
        }
    }, [getToken, username]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMatches(null);

        // If user knows the resource, record the sale directly
        if (knowResource) {
            if (!selectedOffer || !resourceInput || !selectedStep) return;
            setIsLoading(true);
            try {
                const token = await getToken();
                let resourceName = resourceInput;
                if (resourceType === 'video' || resourceType === 'pinComment' || resourceType === 'youtube') {
                    const videoId = extractVideoId(resourceInput);
                    if (!videoId) {
                        alert('Please enter a valid YouTube video URL');
                        setIsLoading(false);
                        return;
                    }
                    resourceName = videoId;
                }
                await axios.post(
                    `https://${SERVER_URL}/api/record-manual-sale/${username}`,
                    {
                        resourceClicked: {
                            type: (resourceType === 'youtube' || resourceType === 'pinComment') ? 'video' : resourceType,
                            name: resourceName
                        },
                        offer: selectedOffer,
                        step: selectedStep,
                        price: customConversionValue ? parseFloat(customConversionValue) : undefined
                    },
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );

                // Reset form
                setEmail('');
                setSelectedOffer('');
                setSelectedStep('');
                setResourceInput('');
                setCustomConversionValue('');
                setKnowResource(false);
                alert('Conversion recorded successfully!');
            } catch (error) {
                console.error('Error recording manual sale:', error);
                alert('Error recording sale. Please try again.');
            } finally {
                setIsLoading(false);
            }
            return;
        }

        // Otherwise, run the email-to-lead matching flow
        setIsLoading(true);
        try {
            const token = await getToken();
            const response = await axios.post(
                `https://${SERVER_URL}/api/match-conversion/${userId}`,
                {
                    email,
                    offerName: selectedOffer
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            console.log('Match conversion response:', response.data);
            setMatches(response.data.matches);
        } catch (error) {
            console.error('Error matching conversion:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRecordConversion = async (match) => {
        try {
            const token = await getToken();
            await axios.post(
                `https://${SERVER_URL}/api/record-manual-sale/${username}`,
                {
                    resourceClicked: match.resource,
                    offer: selectedOffer,
                    step: selectedStep,
                    lead_id: match.lead_id,
                    price: customConversionValue ? parseFloat(customConversionValue) : undefined
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            // Clear the form and matches after successful recording
            setMatches(null);
            setEmail('');
            setSelectedOffer('');
            setSelectedStep('');
            setCustomConversionValue('');
            alert('Conversion recorded successfully!');
            
        } catch (error) {
            console.error('Error recording manual sale:', error);
            alert('Error recording sale. Please try again.');
        }
    };

    const handleDismiss = () => {
        setMatches(null);
        setEmail('');
        setSelectedOffer('');
        setSelectedStep('');
        setCustomConversionValue('');
    };

    return (
        <div className="manual-sale-container">
            <div className="manual-sale-form">
                <h2>Record a Conversion Manually</h2>
                <p>Use this if you think a conversion wasn't tracked or the sale was made over the phone.</p>
                <p>If a click is found for this lead, you can record the conversion manually here.</p>
                <p>To make this feature work better, <b>please also place your tracking code on your checkout page, and any other page where you collect emails.</b></p>
                <p><b>Note: This feature won't always work.</b> </p>
                <form onSubmit={handleSubmit}>
                    <div className="manual-sale-form-group">
                        <label>
                            <input
                                type="checkbox"
                                checked={knowResource}
                                onChange={(e) => setKnowResource(e.target.checked)}
                                className="manual-sale-form-input"
                            />{' '}
                            I know which resource the sale came from
                        </label>
                    </div>

                    {!knowResource && (
                        <div className="manual-sale-form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required={!knowResource}
                            />
                        </div>
                    )}

                    <div className="manual-sale-form-group">
                        <label>Offer</label>
                        <select
                            value={selectedOffer}
                            onChange={(e) => {
                                setSelectedOffer(e.target.value);
                                setSelectedStep(''); // Reset step when offer changes
                            }}
                            required
                        >
                            <option value="">Select an offer</option>
                            {offers && offers.map((offer) => (
                                <option key={offer.id} value={offer.name}>
                                    {offer.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedOffer && (
                        <div className="manual-sale-form-group">
                            <label>Conversion Step</label>
                            <select
                                value={selectedStep}
                                onChange={(e) => setSelectedStep(e.target.value)}
                                required
                            >
                                <option value="">Select a step</option>
                                <option value="sale">Sale</option>
                                {(() => {
                                    const selectedOfferObj = offers.find(offer => offer.name === selectedOffer);
                                    if (!selectedOfferObj) return null;
                                    
                                    const steps = [];
                                    
                                    // Add call booking if enabled
                                    if (selectedOfferObj.call_booking_required) {
                                        steps.push(<option key="call_booking" value="call_booking">Call Booking</option>);
                                    }
                                    
                                    // Add custom funnel steps
                                    if (selectedOfferObj.funnel_steps) {
                                        selectedOfferObj.funnel_steps
                                            .sort((a, b) => a.step_order - b.step_order)
                                            .forEach(step => {
                                                steps.push(
                                                    <option key={step.id} value={step.step_name}>
                                                        {step.step_name}
                                                    </option>
                                                );
                                            });
                                    }
                                    
                                    return steps;
                                })()}
                            </select>
                        </div>
                    )}

                    {knowResource && (
                        <>
                            <div className="manual-sale-form-group">
                                <label>Resource Type</label>
                                <select
                                    value={resourceType}
                                    onChange={(e) => setResourceType(e.target.value)}
                                >
                                    <option value="youtube">YouTube Video</option>
                                    <option value="email">Email</option>
                                    <option value="community">Community</option>
                                    <option value="channel">Channel</option>
                                    <option value="instagram">Instagram</option>
                                    <option value="twitter">Twitter</option>
                                    <option value="linkedin">LinkedIn</option>
                                    <option value="podcast">Podcast</option>
                                    <option value="blog">Blog</option>
                                    <option value="website">Website</option>
                                    <option value="tiktok">TikTok</option>
                                    <option value="ad">Ad</option>
                                    <option value="telegram">Telegram</option>
                                    <option value="facebook">Facebook</option>
                                    <option value="discord">Discord</option>
                                    <option value="pinComment">Pinned Comment</option>
                                </select>
                            </div>
                            <div className="manual-sale-form-group">
                                <label>{(resourceType === 'youtube' || resourceType === 'pinComment') ? 'Video URL' : 'Resource name'}</label>
                                <input
                                    type="text"
                                    value={resourceInput}
                                    onChange={(e) => setResourceInput(e.target.value)}
                                    placeholder={(resourceType === 'youtube' || resourceType === 'pinComment') ? 'https://www.youtube.com/watch?v=...' : 'The name has to match exactly'}
                                    required={knowResource}
                                />
                            </div>
                        </>
                    )}
                    
                    {selectedStep === 'sale' && (
                        <div className="manual-sale-form-group">
                            <label>Custom Conversion Value (optional)</label>
                            <input
                                type="number"
                                value={customConversionValue}
                                onChange={(e) => setCustomConversionValue(e.target.value)}
                                placeholder="Enter custom value"
                                step="0.01"
                                min="0"
                            />
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="manual-sale-submit-button"
                    >
                        {isLoading ? (knowResource ? 'Recording...' : 'Searching...') : (knowResource ? 'Record Conversion' : 'Find Matching Lead')}
                    </button>
                </form>
            </div>

            {!knowResource && matches && (
                <div className="matches-container">
                    {matches.length > 0 ? (
                        matches.every(match => match.has_sale) ? (
                            <>
                                <p>Conversion already recorded for this lead at {new Date(matches[0].timestamp).toLocaleString()}.</p>
                                <p>Note: this may have been from a different email associated with the same lead.</p>
                            </>
                        ) : (
                            matches.filter(match => !match.has_sale).map((match, index) => (
                                <div key={index} className="match-item">
                                    <p>
                                        Click found on {match.resource.type} "{match.resource.name}" for this lead at{' '}
                                        {new Date(match.timestamp).toLocaleString()}
                                    </p>
                                    <div className="action-buttons">
                                        <button 
                                            className="record-button"
                                            onClick={() => handleRecordConversion(match)}
                                        >
                                            Record conversion
                                        </button>
                                        <button 
                                            className="dismiss-button"
                                            onClick={handleDismiss}
                                        >
                                            Do not record conversion
                                        </button>
                                    </div>
                                </div>
                            ))
                        )
                    ) : (
                        <p>No clicks associated with this email found.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default ManualSale;
